import { thumbHashToRGBA } from "thumbhash";

/** How much to pull the placeholder toward grey (0 = untouched, 1 = greyscale).
 *  ThumbHash decodes to vivid, near-saturated colour blobs at tile size — a
 *  strong desaturation turns them into a soft, neutral wash that reads as a
 *  loading preview in both light and dark themes instead of a neon rainbow. */
const DESATURATE = 0.62;
/** Gentle lift so the muted wash doesn't read as muddy/heavy. */
const BRIGHTEN = 1.04;

/**
 * Decode a base64 ThumbHash (as the backend stores it) into a tiny blurred
 * data-URL for use as a tile/preview placeholder. The decoded pixels are
 * desaturated + lifted so the placeholder is a soft, muted preview (not the
 * over-saturated rainbow ThumbHash decodes to). Returns undefined on any bad
 * input — or if a canvas isn't available — so callers fall back to a neutral
 * skeleton. Only the placeholder is affected; the real image is never touched.
 */
/**
 * The source aspect ratio (w / h) encoded in a ThumbHash. Used to size a
 * masonry tile to roughly the right shape before the full image loads, so the
 * column packs without a big reflow. Returns undefined on bad input.
 */
export function thumbhashAspect(hash: string | undefined | null): number | undefined {
  if (!hash) return undefined;
  try {
    const binary = atob(hash);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    const { w, h } = thumbHashToRGBA(bytes);
    return w > 0 && h > 0 ? w / h : undefined;
  } catch {
    return undefined;
  }
}

export function thumbhashToDataUrl(hash: string | undefined | null): string | undefined {
  if (!hash) return undefined;
  try {
    const binary = atob(hash);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);

    const { w, h, rgba } = thumbHashToRGBA(bytes);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    // Mute each pixel toward its luminance, then lift slightly. Luminance-based
    // desaturation keeps the soft tonal shape while killing the vivid hues.
    for (let i = 0; i < rgba.length; i += 4) {
      const r = rgba[i];
      const g = rgba[i + 1];
      const b = rgba[i + 2];
      const lum = 0.299 * r + 0.587 * g + 0.114 * b;
      rgba[i] = Math.min(255, (r + (lum - r) * DESATURATE) * BRIGHTEN);
      rgba[i + 1] = Math.min(255, (g + (lum - g) * DESATURATE) * BRIGHTEN);
      rgba[i + 2] = Math.min(255, (b + (lum - b) * DESATURATE) * BRIGHTEN);
    }

    ctx.putImageData(new ImageData(new Uint8ClampedArray(rgba), w, h), 0, 0);
    return canvas.toDataURL();
  } catch {
    return undefined;
  }
}
